import { Component } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ApiService } from '../services/api.service';
import { HttpErrorResponse } from '@angular/common/http';
import { NgIf } from '@angular/common';
import { Web2ServiceService } from '../services/web2-service.service';
import { AuthService } from '../services/auth.service';
import { WalletService } from '../services/wallet.service';
import { EmailVerificationState } from '../common/constants/common.constants';
import { ToastService } from '../services/toast.service';

@Component({
  selector: 'app-verify-email',
  imports: [RouterModule, NgIf],
  templateUrl: './verify-email.component.html',
  styleUrl: './verify-email.component.css'
})
export class VerifyEmailComponent {
  EmailVerificationState = EmailVerificationState; // Expose to HTML
  isEmailVerified: EmailVerificationState = EmailVerificationState.LOADING;
  error_message: string | null = ""; 
  verifyToken: string | null = null;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private service: ApiService,
    private auth: AuthService,
    private wallet: WalletService,
    private web2: Web2ServiceService,
    private toastr: ToastService
  ) {
    let params: any = this.route.snapshot.queryParams;
    let pathname: string = window.location.pathname;
    
    if (pathname == "/verify-email" && params.token) {
      this.verifyToken = params.token;
      this.isEmailVerified = EmailVerificationState.LOADING; // Start with loading
      if (this.verifyToken) {
        this.verifyEmailToken(this.verifyToken); // Automatically verify
      }
    }
    else if (pathname == "/callback") {
      this.web2.init();
      this.initiateSocialLogin()
    }
    else {
      this.error_message = "Session Timeouted";
      this.isEmailVerified = EmailVerificationState.ERROR;
    }
  }

  verifyEmailToken(token: string): void {
    this.service
      .verifyEmailToken(token)
      .subscribe((result: any) => {
        this.isEmailVerified = EmailVerificationState.SUCCESS;
        this.toastr.success("Email verified successfully!");

        // Keep local auth state aligned immediately after verification so the UI
        // does not continue treating the email as pending.
        let userData = this.auth.getUserData();
        const verifiedUser = result?.data?.user;
        const verifiedEmail = verifiedUser?.email || null;
        const verifiedUserId = verifiedUser?.id || verifiedUser?.user_id || null;
        const currentUserId = userData?.id || userData?.user_id || null;
        const currentPendingEmail = userData?.pending_email || null;
        const currentEmail = userData?.email || null;
        const hasComparableUserIds = !!verifiedUserId && !!currentUserId;
        const isCurrentSessionVerification = !!userData && (
          hasComparableUserIds
            ? String(verifiedUserId) === String(currentUserId)
            : (!!verifiedEmail && (verifiedEmail === currentPendingEmail || verifiedEmail === currentEmail))
        );

        if (verifiedUserId || verifiedEmail) {
          this.auth.setEmailVerifiedLocally({
            userId: verifiedUserId,
            email: verifiedEmail
          });
        }

        if (isCurrentSessionVerification) {
          const emailToApply = verifiedEmail || currentPendingEmail || currentEmail || null;
          
          // Preserve username from local storage in case the backend returns a partial user object
          const existingUserName = userData?.user_name || verifiedUser?.user_name || '';
          const isExistingUserFlag = userData?.is_existing_user || verifiedUser?.is_existing_user || false;

          const updatedUserData = {
            ...userData,
            ...(verifiedUser || {}),
            user_name: existingUserName,
            username: existingUserName,
            is_existing_user: isExistingUserFlag,
            email: emailToApply,
            pending_email: null,
            is_email_verified: true
          };

          // If the user already has a username and their email is now verified, trading is enabled
          if (existingUserName && updatedUserData.is_email_verified) {
            updatedUserData.enable_trading = true;
          } else if (isExistingUserFlag && updatedUserData.is_email_verified) {
            updatedUserData.enable_trading = true;
          }

          this.auth.setUserData(updatedUserData);
        }

        if (this.auth.getSessionToken()) {
          if (!this.auth.isTradeEnabled()) {
            this.auth.sendOperationSignal("enable-trading");
          } else {
            // Redirect directly to home if the user is already fully enabled
            this.router.navigate(['/']);
          }
        }
      }, (error: HttpErrorResponse) => {
        if (error.status == 401) {
          this.error_message = error.error.error_message;
        }
        this.isEmailVerified = EmailVerificationState.ERROR;
      })
  }

  async initiateSocialLogin(): Promise<void> {
    try {
      let result: any = await this.web2.getOAuthResult();
      this.auth.setSessionToken(result.magic.idToken);

      if (this.auth.getSessionToken()) {
        this.service
          .authenticateWeb2User()
          .subscribe((result: any) => {
            if (result.data.two_factor_required && result.data.two_factor_required === true) {
              this.auth.setTempSessionTOken(result.data.jwt_token);
              this.router.navigate(["/"]);
              setTimeout(() => {
                this.auth.sendOperationSignal("login");
              }, 5000);
            }
            else {
              this.auth.login(result.data.jwt_token);
              this.auth.setUserData(result.data.user);
              this.router.navigate(["/"]);
              if (!this.auth.isTradeEnabled()) {
                this.auth.sendOperationSignal("enable-trading");
              }
            }
          });
      }
    }
    catch(e: any) {
      console.log(e);
      this.auth.removeSessionToken();
      this.isEmailVerified = EmailVerificationState.WEB2_AUTH_ERROR;
      // error handler
    }
  }

  async enableTradeOptionForWeb2(): Promise<void> {
    let userdata: any = this.auth.getUserData();
    try {
      let signature: any = await this.web2.generateSignature(userdata.public_address);
      if (signature) {
        let parsedData = this.wallet.getSignatureParams(signature as `0x${string}`);
        this.service.enableTrading(parsedData)
          .subscribe((result: any) => {
            userdata.enable_trading = true;
            this.auth.setUserData(userdata);
            this.toastr.success("Trading enabled successfully.");
          }, (error: HttpErrorResponse) => {
            console.log(error);
            this.toastr.error("Failed to enable trading. Please try again.");
          });
      }
    } catch (error: any) {
      console.log(error);
      const errorMsg = error?.message?.toLowerCase() || '';
      if (error?.code === 4001 || errorMsg.includes('user denied') || errorMsg.includes('rejected') || errorMsg.includes('user cancelled')) {
        this.toastr.error("Signature request was cancelled.");
      }
    }
  }
}
